import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GroupIcon from '@mui/icons-material/Group';
import {
  JOB_STATUS_COLORS,
  APPLICANT_STATUS_COLORS,
  APPLICANT_SOURCES,
  formatEnumLabel,
} from '../../../../constants/hrm';
import {
  useJob, useUpdateJob,
  useCreateApplicant,
} from '../../../../hooks/useHRM';
import { hasPermission } from '../../../../utils/permissionUtils';

const JobDetail = () => {
  const { chatbotId, jobId } = useParams();
  const navigate = useNavigate();

  const [applicantPage, setApplicantPage] = useState(0);
  const [applicantRowsPerPage, setApplicantRowsPerPage] = useState(10);
  const [addApplicantOpen, setAddApplicantOpen] = useState(false);
  const [error, setError] = useState(null);
  const [applicantForm, setApplicantForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: 'CAREER_PAGE',
    resume_url: '',
    current_company: '',
    experience_years: '',
    expected_salary: '',
  });

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const { data: jobData, isLoading: loading, refetch: refetchJob } = useJob(chatbotId, jobId, { enabled: !!chatbotId && !!jobId && canView });

  const job = jobData?.data || null;
  const applicants = job?.applicants || [];
  const applicantTotal = applicants.length;

  const updateJob = useUpdateJob(chatbotId);
  const createApplicant = useCreateApplicant(chatbotId);

  const saving = createApplicant.isPending;

  const handleStatusChange = async (newStatus) => {
    try {
      await updateJob.mutateAsync({ id: Number(jobId), status: newStatus });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update job status');
    }
  };

  const handleAddApplicant = async () => {
    try {
      const payload = {
        ...applicantForm,
        job_id: Number(jobId),
        experience_years: applicantForm.experience_years ? Number(applicantForm.experience_years) : null,
        expected_salary: applicantForm.expected_salary ? Number(applicantForm.expected_salary) : null,
      };
      await createApplicant.mutateAsync(payload);
      setAddApplicantOpen(false);
      setApplicantForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        source: 'CAREER_PAGE',
        resume_url: '',
        current_company: '',
        experience_years: '',
        expected_salary: '',
      });
      refetchJob();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add applicant');
    }
  };

  const handleApplicantFormChange = (field, value) => {
    setApplicantForm((prev) => ({ ...prev, [field]: value }));
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view the HRM module.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/hrm/jobs`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {job?.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={job?.status}
              size="small"
              color={JOB_STATUS_COLORS[job?.status] || 'default'}
            />
            {job?.priority && (
              <Chip label={`Priority: ${job.priority}`} size="small" variant="outlined" />
            )}
            {job?.employment_type && (
              <Chip label={formatEnumLabel(job.employment_type)} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetchJob()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status Management Buttons */}
      {canModify && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {job?.status !== 'OPEN' && (
            <Button variant="outlined" color="success" size="small" onClick={() => handleStatusChange('OPEN')}>
              Open
            </Button>
          )}
          {job?.status !== 'ON_HOLD' && job?.status !== 'CLOSED' && (
            <Button variant="outlined" color="warning" size="small" onClick={() => handleStatusChange('ON_HOLD')}>
              Put on Hold
            </Button>
          )}
          {job?.status !== 'CLOSED' && (
            <Button variant="outlined" color="error" size="small" onClick={() => handleStatusChange('CLOSED')}>
              Close
            </Button>
          )}
          {job?.status !== 'FILLED' && (
            <Button variant="outlined" color="info" size="small" onClick={() => handleStatusChange('FILLED')}>
              Mark as Filled
            </Button>
          )}
        </Box>
      )}

      {/* Job Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Job Details
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOnIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">Location</Typography>
                <Typography variant="body1">
                  {job?.location || 'Not specified'}
                  {job?.is_remote && ' (Remote)'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WorkIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">Department</Typography>
                <Typography variant="body1">{job?.department_name || 'Not specified'}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AttachMoneyIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">Salary Range</Typography>
                <Typography variant="body1">
                  {job?.salary_min && job?.salary_max
                    ? `${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`
                    : job?.salary_min
                    ? `From ${job.salary_min.toLocaleString()}`
                    : job?.salary_max
                    ? `Up to ${job.salary_max.toLocaleString()}`
                    : 'Not specified'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <GroupIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">Vacancies</Typography>
                <Typography variant="body1">{job?.vacancies || 0}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Posted Date</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {job?.posted_date ? new Date(job.posted_date).toLocaleDateString() : 'Not posted'}
            </Typography>
            <Typography variant="body2" color="text.secondary">Closing Date</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {job?.closing_date ? new Date(job.closing_date).toLocaleDateString() : 'Not set'}
            </Typography>
            <Typography variant="body2" color="text.secondary">Visibility</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              {job?.is_internal && <Chip label="Internal" size="small" variant="outlined" />}
              {job?.is_external && <Chip label="External" size="small" variant="outlined" />}
            </Box>
          </Grid>
          {job?.description && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Description</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{job.description}</Typography>
            </Grid>
          )}
          {job?.requirements && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Requirements</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{job.requirements}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Applicants */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Applicants ({applicantTotal})
          </Typography>
          {canModify && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddApplicantOpen(true)}
            >
              Add Applicant
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Applied Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applicants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No applicants yet for this job.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                applicants
                  .slice(applicantPage * applicantRowsPerPage, applicantPage * applicantRowsPerPage + applicantRowsPerPage)
                  .map((applicant) => (
                    <TableRow
                      key={applicant.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(`/chatbot/${chatbotId}/hrm/applicants/${applicant.id}`)
                      }
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {applicant.first_name} {applicant.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{applicant.email}</TableCell>
                      <TableCell>{applicant.source || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={applicant.status}
                          size="small"
                          sx={{
                            backgroundColor: APPLICANT_STATUS_COLORS[applicant.status] || '#9e9e9e',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell>{applicant.score || '-'}</TableCell>
                      <TableCell>
                        {applicant.applied_date
                          ? new Date(applicant.applied_date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/chatbot/${chatbotId}/hrm/applicants/${applicant.id}`);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          {applicants.length > 0 && (
            <TablePagination
              component="div"
              count={applicantTotal}
              page={applicantPage}
              onPageChange={(e, newPage) => setApplicantPage(newPage)}
              rowsPerPage={applicantRowsPerPage}
              onRowsPerPageChange={(e) => {
                setApplicantRowsPerPage(parseInt(e.target.value, 10));
                setApplicantPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </TableContainer>
      </Paper>

      {/* Add Applicant Dialog */}
      <Dialog open={addApplicantOpen} onClose={() => setAddApplicantOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Applicant</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={applicantForm.first_name}
                onChange={(e) => handleApplicantFormChange('first_name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={applicantForm.last_name}
                onChange={(e) => handleApplicantFormChange('last_name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={applicantForm.email}
                onChange={(e) => handleApplicantFormChange('email', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                value={applicantForm.phone}
                onChange={(e) => handleApplicantFormChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={applicantForm.source}
                  label="Source"
                  onChange={(e) => handleApplicantFormChange('source', e.target.value)}
                >
                  {APPLICANT_SOURCES.map((s) => (
                    <MenuItem key={s} value={s}>{formatEnumLabel(s)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Resume URL"
                value={applicantForm.resume_url}
                onChange={(e) => handleApplicantFormChange('resume_url', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Company"
                value={applicantForm.current_company}
                onChange={(e) => handleApplicantFormChange('current_company', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Years of Experience"
                type="number"
                value={applicantForm.experience_years}
                onChange={(e) => handleApplicantFormChange('experience_years', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Expected Salary"
                type="number"
                value={applicantForm.expected_salary}
                onChange={(e) => handleApplicantFormChange('expected_salary', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddApplicantOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddApplicant}
            disabled={saving || !applicantForm.first_name || !applicantForm.last_name || !applicantForm.email}
          >
            {saving ? <CircularProgress size={24} /> : 'Add Applicant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobDetail;
